// Programmation des approvisionnements (D1) et cargaisons (D2) : états, date actualisée, tableau à 7, 15, 30,
// 60 et 90 jours comparé aux besoins estimés (consommation moyenne observée tant que les prévisions du lot 5
// ne sont pas construites). Les volumes annoncés ne sont jamais présentés comme reçus.

import { fois, moins, plus } from './decimal-outils';

export const ETATS_PROGRAMMATION = ['commande', 'annonce', 'expedie', 'arrive', 'receptionne', 'utilisable', 'annule'] as const;
export type EtatProgrammation = (typeof ETATS_PROGRAMMATION)[number];

export const LIBELLES_ETATS_PROGRAMMATION: Record<EtatProgrammation, string> = {
  commande: 'Commandé',
  annonce: 'Annoncé',
  expedie: 'Expédié',
  arrive: 'Arrivé',
  receptionne: 'Réceptionné',
  utilisable: 'Utilisable',
  annule: 'Annulé',
};

/** États d'un volume encore attendu (ni reçu, ni annulé). */
export const ETATS_PROGRAMMATION_ATTENDUS: readonly EtatProgrammation[] = ['commande', 'annonce', 'expedie', 'arrive'];

/** Statuts des cargaisons (page 23 du dossier). */
export const STATUTS_CARGAISON = [
  'programme',
  'confirme',
  'en_transport',
  'en_attente_accostage',
  'en_dechargement',
  'reception_terminee',
  'controle_en_cours',
  'cloture',
  'annule',
] as const;
export type StatutCargaison = (typeof STATUTS_CARGAISON)[number];

export const LIBELLES_STATUTS_CARGAISON: Record<StatutCargaison, string> = {
  programme: 'Programmée',
  confirme: 'Confirmée',
  en_transport: 'En transport',
  en_attente_accostage: 'En attente d’accostage',
  en_dechargement: 'En déchargement',
  reception_terminee: 'Réception terminée',
  controle_en_cours: 'Contrôle en cours',
  cloture: 'Clôturée',
  annule: 'Annulée',
};

/** Transitions autorisées du cycle de statuts d'une cargaison. */
export const TRANSITIONS_CARGAISON: Record<StatutCargaison, readonly StatutCargaison[]> = {
  programme: ['confirme', 'annule'],
  confirme: ['en_transport', 'annule'],
  en_transport: ['en_attente_accostage', 'en_dechargement', 'annule'],
  en_attente_accostage: ['en_dechargement', 'annule'],
  en_dechargement: ['reception_terminee'],
  reception_terminee: ['controle_en_cours', 'cloture'],
  controle_en_cours: ['cloture'],
  cloture: [],
  annule: [],
};

export function transitionCargaisonAutorisee(de: StatutCargaison, vers: StatutCargaison): boolean {
  return TRANSITIONS_CARGAISON[de].includes(vers);
}

/** Correspondance entre le statut d'une cargaison et l'état de la ligne de programmation liée. */
export function etatProgrammationPourCargaison(s: StatutCargaison): EtatProgrammation {
  switch (s) {
    case 'programme':
      return 'commande';
    case 'confirme':
      return 'annonce';
    case 'en_transport':
      return 'expedie';
    case 'en_attente_accostage':
    case 'en_dechargement':
      return 'arrive';
    case 'reception_terminee':
    case 'controle_en_cours':
      return 'receptionne';
    case 'cloture':
      return 'utilisable';
    case 'annule':
      return 'annule';
  }
}

export interface LigneProgrammee {
  produit: string;
  unite: string;
  quantite: string;
  /** Date confirmée si elle existe, sinon date estimée. */
  dateActualisee: string | Date;
  etat: EtatProgrammation;
}

export interface CelluleHorizon {
  horizonJours: number;
  attendu: string;
  enRetard: string;
  /** Besoin estimé sur l'horizon ; null si la consommation est inconnue. */
  besoin: string | null;
  /** Stock actuel + attendu - besoin ; null si le stock ou le besoin est inconnu. */
  solde: string | null;
}

export interface LigneTableauHorizons {
  produit: string;
  unite: string;
  stock: string | null;
  consommationJournaliere: string | null;
  horizons: CelluleHorizon[];
  parEtat: Partial<Record<EtatProgrammation, string>>;
}

export const HORIZONS_PROGRAMMATION = [7, 15, 30, 60, 90] as const;

/**
 * Tableau des horizons : volumes attendus dont la date actualisée tombe dans l'horizon, volumes en retard
 * (date dépassée sans réception), besoin = consommation journalière × jours, solde = stock + attendu - besoin.
 */
export function tableauHorizons(
  lignes: readonly LigneProgrammee[],
  references: readonly { produit: string; unite: string; stock: string | null; consommationJournaliere: string | null }[],
  maintenant: Date,
  horizons: readonly number[] = HORIZONS_PROGRAMMATION,
): LigneTableauHorizons[] {
  const produits = new Map<string, { produit: string; unite: string }>();
  for (const r of references) produits.set(`${r.produit}|${r.unite}`, r);
  for (const l of lignes) produits.set(`${l.produit}|${l.unite}`, { produit: l.produit, unite: l.unite });
  const debut = maintenant.getTime();
  return [...produits.values()]
    .sort((a, b) => a.produit.localeCompare(b.produit))
    .map(({ produit, unite }) => {
      const ref = references.find((r) => r.produit === produit && r.unite === unite);
      const propres = lignes.filter((l) => l.produit === produit && l.unite === unite);
      const parEtat: Partial<Record<EtatProgrammation, string>> = {};
      for (const l of propres) parEtat[l.etat] = plus(parEtat[l.etat] ?? '0', l.quantite);
      const enRetard = propres
        .filter((l) => ETATS_PROGRAMMATION_ATTENDUS.includes(l.etat) && new Date(l.dateActualisee).getTime() < debut)
        .reduce((s, l) => plus(s, l.quantite), '0');
      const cellules = horizons.map((h): CelluleHorizon => {
        const fin = debut + h * 86_400_000;
        const attendu = propres
          .filter((l) => {
            const t = new Date(l.dateActualisee).getTime();
            return ETATS_PROGRAMMATION_ATTENDUS.includes(l.etat) && t >= debut && t <= fin;
          })
          .reduce((s, l) => plus(s, l.quantite), '0');
        const besoin = ref?.consommationJournaliere ? fois(ref.consommationJournaliere, String(h)) : null;
        const solde = besoin !== null && ref?.stock ? moins(plus(ref.stock, attendu), besoin) : null;
        return { horizonJours: h, attendu, enRetard, besoin, solde };
      });
      return { produit, unite, stock: ref?.stock ?? null, consommationJournaliere: ref?.consommationJournaliere ?? null, horizons: cellules, parEtat };
    });
}
